import { Component, HostBinding, OnInit, ViewEncapsulation } from "@angular/core"
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router"
import { NgbTooltipConfig } from "@ng-bootstrap/ng-bootstrap"
import { NotificationsService } from "angular2-notifications"
import { PendoService } from "./services/pendo.service"
import { UserService } from "./services/user.service"
import { ModalService } from "./shared-components/modal/modal.service"
import { AuthModalComponent } from "./shared-components/modals/auth-modal/auth-modal.component"
import { StudentLoginModalComponent } from "./shared-components/modals/student-login-modal/student-login-modal.component"
import { redirectToError } from "./utils/error-handling"
import Utils from "./utils/utils"
import { Location } from "@angular/common"

declare var window: any

@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
    encapsulation: ViewEncapsulation.None,
})
export class AppComponent implements OnInit {
    @HostBinding("class") hostBinding = "d-block h-100"

    constructor(
        private router: Router,
        private activeRoute: ActivatedRoute,
        private modalService: ModalService,
        private userService: UserService,
        private notificationService: NotificationsService,
        private pendoService: PendoService,
        private tooltipConfig: NgbTooltipConfig,
        private location: Location
    ) {
        window.PBSLM.browser = {
            ie: Utils.isIEBrowser(),
            safari: Utils.isSafari(),
            edge: Utils.isEdgeBrowser(),
        }
        if (window.PBSLM.ERROR_CODE) {
            redirectToError(this.router, this.location, window.PBSLM.ERROR_CODE)
            window.PBSLM.ERROR_CODE = null
            return
        }

        this.configureTooltip()
    }

    public ngOnInit() {
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                //this part was change because of the new RouterReuseStrategy behavior,
                // todo: additional investigation in Hack Days
                const url = new URL(this.router.url, window.location.origin)
                const urlFragments = url.pathname.split("/")
                const studentQueryParam = !!url.searchParams.get("student")
                if (urlFragments[1] && urlFragments[1] === "") {
                    // Homepage case
                    this.userService.setStudentExperience(false)
                } else if (urlFragments[1] && urlFragments[1] === "student") {
                    // Student urls
                    this.userService.setStudentExperience(true)
                } else if (this.userService.isStudent() && !studentQueryParam) {
                    // student is logged in, should always have student experience and student=true
                    // but we need to keep existing params for error navigation for example
                    this.router.navigate([], {
                        queryParams: { student: true },
                        queryParamsHandling: "merge",
                        skipLocationChange: true,
                    })
                } else {
                    // student=true url case
                    this.userService.setStudentExperience(studentQueryParam)
                }
            }
        })

        this.userService.runOnceForQueryParam(this.activeRoute, "login", () => {
            if (!this.userService.isLoggedIn()) {
                if (this.userService.hasStudentExperience()) {
                    this.modalService.open(StudentLoginModalComponent)
                } else {
                    this.modalService.open(AuthModalComponent)
                }
            }
        })
        this.pendoService.initialize()
        this.scrollToTopBeforeUnload()
    }

    private scrollToTopBeforeUnload() {
        window.onbeforeunload = () => {
            document.querySelector("html").style.scrollBehavior = "auto"
            document.documentElement.scrollTop = 0
        }
    }

    public onActivate() {
        window.scroll(0, 0)
        this.preventEmbedCycles()
        this.showServerSetMessages()
    }

    private preventEmbedCycles() {
        try {
            if (window.top !== window.self && window.top.location.host === window.location.host) {
                /* The current application is embeded in an iframe, aka "double header bug", most likely due to an
                 * old site navigation issue. We want to load the most recent viewed page in the top browser window.
                 * We also, only want to do this the current application is embedded on the same domain.
                 */
                window.top.location.href = window.self.location.href
            }
        } catch (err) {
            console.info("Cannot access window top from iframe. Skipping preventEmbedCycles")
        }
    }

    private showServerSetMessages() {
        if (window.PBSLM.NG.MESSAGES) {
            window.PBSLM.NG.MESSAGES.forEach((item) => this.showMessage(item))
            window.PBSLM.NG.MESSAGES = null
        }
    }

    private showMessage(messageItem) {
        const params = { timeOut: 10000, showProgressBar: false }
        let type
        if (messageItem.tags.indexOf("error") !== -1) {
            type = "error"
        } else if (messageItem.tags.indexOf("success") !== -1) {
            type = "success"
        } else if (messageItem.tags.indexOf("warn") !== -1) {
            type = "warn"
        } else {
            type = "info"
        }
        this.notificationService.html(messageItem.message, type, params)
    }

    private configureTooltip() {
        this.tooltipConfig.triggers = "hover"
        this.tooltipConfig.openDelay = 300
        this.tooltipConfig.container = "body"
        this.tooltipConfig.placement = "top"
    }
}
